/**
 * Planning sportif — §5. Le sport n'est pas un calendrier séparé : c'est une
 * contrainte intelligente du planning de vie. On génère la semaine (le moteur
 * répartit les séances sur les jours disponibles en évitant deux séances
 * lourdes consécutives), puis on visualise la grille hebdomadaire. Une séance
 * peut être replanifiée à une autre date.
 */
import React, { useEffect, useState } from 'react'
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  TextField,
  Typography,
} from '@mui/material'
import AutoAwesomeRoundedIcon from '@mui/icons-material/AutoAwesomeRounded'

import { performanceApi } from '../api/performanceApi'
import {
  Loading,
  PERF_ACCENT,
  SESSION_TYPE_LABELS,
  WEEKDAYS,
  cardSx,
} from './shared'

const SESSION_COLOR = {
  strength: '#ff6a3d',
  endurance: '#5b8cff',
  interval: '#ff4d4f',
  long: '#9b6bff',
  technique: '#3ddc97',
  mobility: '#2dd4bf',
  recovery: '#94a3b8',
  test: '#fbbf24',
  rest: 'rgba(255,255,255,0.3)',
}

function mondayOf(d) {
  const date = new Date(d)
  const day = (date.getDay() + 6) % 7 // 0 = lundi
  date.setDate(date.getDate() - day)
  date.setHours(0, 0, 0, 0)
  return date
}

function fmt(date) {
  return date.toISOString().slice(0, 10)
}

export default function PlanningView() {
  const [weekStart, setWeekStart] = useState(mondayOf(new Date()))
  const [sessions, setSessions] = useState([])
  const [loading, setLoading] = useState(true)
  const [generating, setGenerating] = useState(false)
  const [reschedule, setReschedule] = useState(null) // session
  const [newDate, setNewDate] = useState('')
  const [busy, setBusy] = useState(false)

  const weekEnd = new Date(weekStart)
  weekEnd.setDate(weekEnd.getDate() + 6)

  const load = async () => {
    setLoading(true)
    const s = await performanceApi.sessions.list(
      `?from=${fmt(weekStart)}&to=${fmt(weekEnd)}`,
    )
    setSessions(s)
    setLoading(false)
  }

  useEffect(() => {
    load()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [weekStart])

  const generate = async () => {
    setGenerating(true)
    try {
      await performanceApi.generatePlan({
        week_start: fmt(weekStart),
        replace: true,
      })
      await load()
    } finally {
      setGenerating(false)
    }
  }

  const doReschedule = async () => {
    if (!reschedule || !newDate) return
    setBusy(true)
    try {
      await performanceApi.rescheduleSession(reschedule.id, {
        scheduled_date: newDate,
      })
      setReschedule(null)
      setNewDate('')
      await load()
    } finally {
      setBusy(false)
    }
  }

  const shiftWeek = (delta) => {
    const d = new Date(weekStart)
    d.setDate(d.getDate() + delta * 7)
    setWeekStart(mondayOf(d))
  }

  const byDay = WEEKDAYS.map((_, i) => {
    const day = new Date(weekStart)
    day.setDate(day.getDate() + i)
    const iso = fmt(day)
    return {
      label: WEEKDAYS[i],
      date: iso,
      dayNum: day.getDate(),
      items: sessions.filter((s) => s.scheduled_date === iso),
    }
  })

  return (
    <Box>
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        flexWrap="wrap"
        useFlexGap
        sx={{ mb: 2 }}
      >
        <Stack direction="row" spacing={1} alignItems="center">
          <Button onClick={() => shiftWeek(-1)} sx={{ color: '#fff', minWidth: 36 }}>
            ‹
          </Button>
          <Typography sx={{ color: '#fff', fontWeight: 700 }}>
            Semaine du {fmt(weekStart)}
          </Typography>
          <Button onClick={() => shiftWeek(1)} sx={{ color: '#fff', minWidth: 36 }}>
            ›
          </Button>
        </Stack>
        <Button
          startIcon={<AutoAwesomeRoundedIcon />}
          onClick={generate}
          disabled={generating}
          sx={{
            bgcolor: PERF_ACCENT,
            color: '#0b0f1f',
            fontWeight: 700,
            textTransform: 'none',
            '&:hover': { bgcolor: '#ff7d56' },
          }}
        >
          {generating ? 'Génération…' : 'Générer la semaine'}
        </Button>
      </Stack>

      {loading ? (
        <Loading label="Chargement du planning…" />
      ) : (
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', sm: 'repeat(7, 1fr)' },
            gap: 1.5,
          }}
        >
          {byDay.map((d) => (
            <Box
              key={d.date}
              sx={{
                ...cardSx,
                p: 1.5,
                minHeight: 140,
              }}
            >
              <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="baseline"
                sx={{ mb: 1 }}
              >
                <Typography
                  sx={{ color: 'rgba(255,255,255,0.7)', fontSize: 12, fontWeight: 600 }}
                >
                  {d.label}
                </Typography>
                <Typography sx={{ color: 'rgba(255,255,255,0.4)', fontSize: 12 }}>
                  {d.dayNum}
                </Typography>
              </Stack>
              <Stack spacing={1}>
                {d.items.map((s) => (
                  <Box
                    key={s.id}
                    onClick={() => {
                      setReschedule(s)
                      setNewDate(s.scheduled_date)
                    }}
                    sx={{
                      bgcolor: `${SESSION_COLOR[s.session_type] || PERF_ACCENT}22`,
                      borderLeft: `3px solid ${
                        SESSION_COLOR[s.session_type] || PERF_ACCENT
                      }`,
                      borderRadius: 1,
                      p: 1,
                      cursor: 'pointer',
                    }}
                  >
                    <Typography sx={{ color: '#fff', fontSize: 12, fontWeight: 600 }}>
                      {s.title}
                    </Typography>
                    <Typography
                      sx={{ color: 'rgba(255,255,255,0.55)', fontSize: 11 }}
                    >
                      {SESSION_TYPE_LABELS[s.session_type] || s.session_type}
                      {s.planned_load ? ` · ${s.planned_load}/10` : ''}
                    </Typography>
                  </Box>
                ))}
              </Stack>
            </Box>
          ))}
        </Box>
      )}

      <Dialog open={!!reschedule} onClose={() => setReschedule(null)}>
        <DialogTitle>Déplacer — {reschedule?.title}</DialogTitle>
        <DialogContent>
          <TextField
            type="date"
            label="Nouvelle date"
            InputLabelProps={{ shrink: true }}
            value={newDate}
            onChange={(e) => setNewDate(e.target.value)}
            fullWidth
            sx={{ mt: 1, minWidth: 280 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setReschedule(null)}>Annuler</Button>
          <Button
            onClick={doReschedule}
            disabled={busy}
            variant="contained"
            sx={{ bgcolor: PERF_ACCENT, color: '#0b0f1f' }}
          >
            Déplacer
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}
